import type { Module, School } from './schools.model';
import { resolvePricingYear, type ResolveOptions } from './school-pricing.utils';

export interface ModuleFilterOptions extends ResolveOptions {
  /**
   * Modul, das sichtbar bleiben muss, auch wenn es zu einer anderen Schiene
   * gehört (z.B. das bereits zugewiesene Modul eines Kindes) — sonst zeigt die
   * Auswahl den aktuellen Zustand nicht mehr an.
   */
  keepModuleId?: string | null;
}

/**
 * Schuljahr (= Preisschiene), aus dem die wählbaren Module stammen (#746).
 * null = keine Schiene auflösbar (Schule nicht migriert oder kein Jahr gewählt).
 */
export function resolveModulePricingYear(
  school: School | null | undefined,
  schoolYear: number | null | undefined,
  opts: ResolveOptions = {},
): number | null {
  if (!school || schoolYear === null || schoolYear === undefined) return null;
  return resolvePricingYear(school, schoolYear, opts);
}

/**
 * Module auf die Schiene des gewählten Schuljahres einschränken (#887).
 * Ohne diesen Filter zeigt eine Modul-Auswahl alle Schienen der Schule
 * gleichzeitig, sobald für ein weiteres Jahr ein Entwurf hinterlegt ist.
 */
export function filterModulesForSchoolYear(
  modules: Module[],
  school: School | null | undefined,
  schoolYear: number | null | undefined,
  opts: ModuleFilterOptions = {},
): Module[] {
  const { keepModuleId, ...resolveOpts } = opts;
  const pricingYear = resolveModulePricingYear(school, schoolYear, resolveOpts);
  if (pricingYear === null) return modules;

  // Nicht migrierte Module (ohne schoolYear) gehören zur einzigen Schiene.
  const filtered = modules.filter(
    (m) => (m.schoolYear ?? pricingYear) === pricingYear,
  );

  if (keepModuleId && !filtered.some((m) => m.id === keepModuleId)) {
    const current = modules.find((m) => m.id === keepModuleId);
    if (current) return [...filtered, current];
  }
  return filtered;
}
